import { getErrorMessage } from "@/utils/errorHelpers";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminKycAPI } from "@/services/api";
import { toast } from "sonner";
import { formatDate } from "@/utils/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ShieldCheck, Clock, CheckCircle, XCircle, User, Baby, Image, FileDown, MessageSquare, Send } from "lucide-react";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import ZoomableImage from "@/components/app/ZoomableImage";

export default function AdminKycPage() {
  const [kycs, setKycs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [validating, setValidating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [kycUpdates, setKycUpdates] = useState([]);

  useEffect(() => { loadKycs(); }, []);

  const loadKycs = async () => {
    try {
      const [kycRes, updateRes] = await Promise.all([
        adminKycAPI.list(),
        adminKycAPI.listUpdates(),
      ]);
      setKycs(Array.isArray(kycRes.data) ? kycRes.data : []);
      setKycUpdates(Array.isArray(updateRes.data) ? updateRes.data : []);
    }
    catch (e) { toast.error("Erreur chargement KYC"); }
    finally { setLoading(false); }
  };

  const loadMessages = async (kycId) => {
    try {
      const res = await adminKycAPI.getMessages(kycId);
      setMessages(Array.isArray(res.data) ? res.data : []);
    }
    catch (e) { setMessages([]); }
  };

  const handleSelectKyc = (kyc) => {
    setSelected(kyc);
    loadMessages(kyc.id);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selected) return;
    setSendingMsg(true);
    try {
      await adminKycAPI.sendMessage(selected.id, newMessage);
      setNewMessage("");
      loadMessages(selected.id);
      toast.success("Message envoyé");
    } catch (e) { toast.error("Erreur envoi"); }
    finally { setSendingMsg(false); }
  };

  const handleValidate = async (approved) => {
    if (!selected) return;
    setValidating(true);
    try {
      await adminKycAPI.validate(selected.id, approved, rejectionReason);
      toast.success(approved ? "Dossier KYC approuvé" : "Dossier KYC rejeté");
      setSelected(null); setRejectionReason(""); setMessages([]);
      loadKycs();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
    finally { setValidating(false); }
  };

  const handleProposeUpdate = async (requestId) => {
    try {
      await adminKycAPI.proposeUpdate(requestId, "Pieces verifiees");
      toast.success("Modification proposee");
      loadKycs();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur proposition"));
    }
  };

  const handleApproveUpdate = async (requestId) => {
    try {
      await adminKycAPI.approveUpdate(requestId, "Approbation finale FULL");
      toast.success("Modification approuvee");
      loadKycs();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur approbation"));
    }
  };

  const safeKycs = Array.isArray(kycs) ? kycs : [];
  const safeKycUpdates = Array.isArray(kycUpdates) ? kycUpdates : [];
  const pending = safeKycs.filter((k) => k.status === "pending");
  const approved = safeKycs.filter((k) => k.status === "approved");
  const getValidatorLabel = (kyc) =>
    kyc.validated_by_name ||
    kyc.validated_admin_name ||
    kyc.admin_name ||
    kyc.validated_by ||
    null;

  if (loading) return <LoadingState label="Chargement des dossiers KYC..." />;

  return (
    <PageShell data-testid="admin-kyc-page">
      <PageHeader title="Vérification des partenaires" />

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader><CardTitle className="text-sm font-medium text-[#5C625F]">En attente</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-yellow-600" data-testid="kyc-pending-count">{pending.length}</div></CardContent>
        </Card>
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader><CardTitle className="text-sm font-medium text-[#5C625F]">Approuvés</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-green-600" data-testid="kyc-approved-count">{approved.length}</div></CardContent>
        </Card>
      </div>

      {safeKycUpdates.length > 0 && (
        <Card className="border-[#E6E4E0] shadow-sm mb-8 border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">
              Modifications KYC four-eyes ({safeKycUpdates.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {safeKycUpdates.map((update) => (
              <div key={update.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-[#2A4B46]">{update.user_name}</span>
                      <span className="text-xs text-[#5C625F]">({update.user_id})</span>
                      <Badge className="bg-white text-blue-700">{update.status}</Badge>
                    </div>
                    <p className="text-xs text-[#5C625F] mt-1">Demande: {formatDate(update.created_at)}</p>
                    {update.proposed_by && (
                      <p className="text-xs text-[#5C625F] mt-1">
                        Propose par: {update.proposed_by_name || update.proposed_by}
                      </p>
                    )}
                    {update.history_label && (
                      <p className="text-xs text-green-700 mt-1">{update.history_label}</p>
                    )}
                    <p className="text-xs text-[#5C625F] mt-1">Motif: {update.reason || "-"}</p>
                    <div className="mt-3 grid max-w-sm grid-cols-2 gap-2">
                      {update.requested_changes?.id_photo_path && (
                        <ZoomableImage src={adminKycAPI.getUpdatePhotoUrl(update.id, "id")} alt="Nouvelle pièce d'identité" dialogLabel="Nouvelle pièce d'identité" className="h-24 w-full rounded border border-blue-200" />
                      )}
                      {update.requested_changes?.profile_photo_path && (
                        <ZoomableImage src={adminKycAPI.getUpdatePhotoUrl(update.id, "profile")} alt="Nouvelle photo de profil" dialogLabel="Nouvelle photo de profil" className="h-24 w-full rounded border border-blue-200" />
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2 sm:flex sm:shrink-0">
                    {update.status === "submitted" && (
                      <Button size="sm" className="bg-[#2A4B46] text-white" onClick={() => handleProposeUpdate(update.id)}>
                        Proposer
                      </Button>
                    )}
                    {(update.status === "proposed" || update.status === "pending_admin_validation") && (
                      <Button size="sm" className="bg-green-700 text-white" onClick={() => handleApproveUpdate(update.id)}>
                        Approuver FULL
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <Card className="border-[#E6E4E0] shadow-sm mb-8 border-l-4 border-l-yellow-500">
          <CardHeader><CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Dossiers en attente ({pending.length})</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {pending.map((kyc) => (
              <div key={kyc.id} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid={`kyc-pending-${kyc.id}`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <User size={16} className="text-[#2A4B46]" />
                      <span className="font-semibold text-[#2A4B46]">{kyc.user_name}</span>
                      <span className="text-xs text-[#5C625F]">({kyc.user_id})</span>
                      {kyc.is_minor && <Badge className="bg-orange-100 text-orange-800"><Baby size={12} className="mr-1" /> Mineur</Badge>}
                    </div>
                    <div className="grid gap-1 text-xs text-[#5C625F] sm:grid-cols-2">
                      <p>Nom: {kyc.identity?.nom} {kyc.identity?.postnom} {kyc.identity?.prenom}</p>
                      <p>Naissance: {kyc.identity?.date_naissance}</p>
                      <p>Tel: {kyc.identity?.telephone}</p>
                      <p>ID: {kyc.identity?.numero_identite}</p>
                    </div>
                    <p className="text-xs text-[#5C625F] mt-1">Soumis le {formatDate(kyc.created_at)}</p>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-[#2A4B46] text-white lg:w-auto lg:shrink-0" onClick={() => handleSelectKyc(kyc)} data-testid={`kyc-review-${kyc.id}`}>
                        <ShieldCheck size={16} className="mr-2" /> Examiner
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="h-[100dvh] w-screen max-w-none overflow-y-auto rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl" data-testid="kyc-review-dialog">
                      <DialogHeader><DialogTitle className="text-2xl font-bold text-[#2A4B46] font-['Outfit']">Examiner le dossier KYC</DialogTitle></DialogHeader>
                      {selected && <KycReviewContent
                        kyc={selected} messages={messages} newMessage={newMessage}
                        setNewMessage={setNewMessage} sendingMsg={sendingMsg}
                        handleSendMessage={handleSendMessage} rejectionReason={rejectionReason}
                        setRejectionReason={setRejectionReason} handleValidate={handleValidate}
                        validating={validating}
                      />}
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Approved list */}
      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader><CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Dossiers approuvés ({approved.length})</CardTitle></CardHeader>
        <CardContent>
          {approved.length === 0 ? <p className="text-center text-[#5C625F] py-8">Aucun dossier approuvé</p> : (
            <div className="space-y-3">
              {approved.map((kyc) => (
                <div key={kyc.id} className="flex flex-col gap-3 rounded-xl border border-green-200 bg-green-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="font-semibold text-[#2A4B46]">{kyc.user_name}</span>
                    <span className="text-xs text-[#5C625F]">({kyc.user_id})</span>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <a href={adminKycAPI.getAgreementPdfUrl(kyc.id)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-medium transition-colors"
                      data-testid={`agreement-pdf-${kyc.id}`}>
                      <FileDown size={14} /> Agrément PDF
                    </a>
                    {getValidatorLabel(kyc) && (
                      <span className="text-xs text-[#5C625F]" data-testid={`kyc-validated-by-${kyc.id}`}>
                        validé par: {getValidatorLabel(kyc)}
                      </span>
                    )}
                    <span className="text-xs text-[#5C625F]">{formatDate(kyc.validated_at || kyc.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}

function KycReviewContent({ kyc, messages, newMessage, setNewMessage, sendingMsg, handleSendMessage, rejectionReason, setRejectionReason, handleValidate, validating }) {
  const identity = kyc?.identity || {};
  const address = identity.adresse || {};
  const financial = kyc?.financial || {};
  const safeMessages = Array.isArray(messages) ? messages : [];

  return (
    <div className="space-y-4">
      {/* Identity */}
      <div className="p-3 bg-[#F3EFE9] rounded-lg">
        <p className="font-semibold text-[#2A4B46]">{identity.nom || "-"} {identity.postnom || ""} {identity.prenom || ""}</p>
        <p className="text-sm text-[#5C625F]">{kyc?.user_email || "-"} | {identity.telephone || "-"}</p>
        <p className="text-sm text-[#5C625F]">E-mail verifie: {kyc?.email_verified ? "Oui" : "Non"}{kyc?.email_verified_at ? ` - ${formatDate(kyc.email_verified_at)}` : ""}</p>
        <p className="text-sm text-[#5C625F]">Naissance: {identity.date_naissance || "-"} | Sexe: {identity.sexe || "-"} | État civil: {identity.etat_civil || "-"}</p>
        <p className="text-sm text-[#5C625F]">ID: {identity.numero_identite || "-"} ({identity.type_identite || "-"})</p>
        <p className="text-sm text-[#5C625F]">Adresse: {address.rue || "-"} N{address.numéro || "-"}, {address.commune || "-"}, {address.ville || "-"}, {address.province || "-"}, {address.pays || "-"}</p>
        <p className="text-sm text-[#5C625F]">Parrain: {kyc?.parrain_id || "Aucun"}</p>
      </div>

      {/* Photos */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="border border-[#E6E4E0] rounded-lg overflow-hidden">
          <p className="text-xs font-semibold text-[#2A4B46] p-2 bg-[#F3EFE9] flex items-center gap-1"><Image size={12} /> Carte d'identité</p>
          {kyc.id_photo_path ? (
            <ZoomableImage src={adminKycAPI.getPhotoUrl(kyc.id, "id")} alt="Carte d'identité" dialogLabel="Carte d'identité" className="h-40 w-full" data-testid="kyc-id-photo" />
          ) : (
            <div className="h-40 flex items-center justify-center text-[#5C625F] text-xs">Pas de photo</div>
          )}
        </div>
        <div className="border border-[#E6E4E0] rounded-lg overflow-hidden">
          <p className="text-xs font-semibold text-[#2A4B46] p-2 bg-[#F3EFE9] flex items-center gap-1"><Image size={12} /> Photo de profil</p>
          {kyc.profile_photo_path ? (
            <ZoomableImage src={adminKycAPI.getPhotoUrl(kyc.id, "profile")} alt="Photo de profil" dialogLabel="Photo de profil" className="h-40 w-full" data-testid="kyc-profile-photo" />
          ) : (
            <div className="h-40 flex items-center justify-center text-[#5C625F] text-xs">Pas de photo</div>
          )}
        </div>
      </div>

      {/* Tutor */}
      {kyc.is_minor && kyc.tuteur && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="font-semibold text-orange-800 flex items-center gap-1"><Baby size={14} /> Tuteur</p>
          <p className="text-sm text-orange-700">{kyc.tuteur.nom} {kyc.tuteur.postnom} {kyc.tuteur.prenom}</p>
          <p className="text-sm text-orange-700">{kyc.tuteur.email} | {kyc.tuteur.telephone}</p>
          <p className="text-xs text-orange-600 mt-1">Un compte sera automatiquement créé pour le tuteur si approuvé.</p>
        </div>
      )}

      {/* Financial */}
      <div className="p-3 bg-[#F3EFE9] rounded-lg">
        <p className="text-sm font-semibold text-[#2A4B46]">Mobile Money: {financial.mobile_money_intitule || "-"} — {financial.mobile_money_numero || "-"}</p>
        {financial.banque_nom && <p className="text-sm text-[#5C625F]">Banque: {financial.banque_nom} — {financial.banque_numero || "-"}</p>}
        {kyc?.bénéficiaire_designation && <p className="text-sm text-[#5C625F]">Bénéficiaire: {kyc.bénéficiaire_designation}</p>}
      </div>

      {/* Agreement PDF */}
      <a href={adminKycAPI.getAgreementPdfUrl(kyc.id)} target="_blank" rel="noopener noreferrer"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors"
        data-testid="download-agreement-pdf">
        <FileDown size={16} /> Télécharger l'agrément signé (PDF)
      </a>
      {kyc.activation_payment_proof_path && (
        <a href={adminKycAPI.getActivationPaymentReceiptUrl(kyc.id)} target="_blank" rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg text-sm font-medium transition-colors"
          data-testid="kyc-activation-payment-receipt">
          <FileDown size={16} /> Voir le reçu de paiement d'activation
        </a>
      )}

      {/* Messages */}
      <div className="border border-[#E6E4E0] rounded-lg">
        <div className="p-3 bg-[#F3EFE9] border-b border-[#E6E4E0] flex items-center gap-2">
          <MessageSquare size={14} className="text-[#2A4B46]" />
          <span className="text-sm font-semibold text-[#2A4B46]">Messages ({safeMessages.length})</span>
        </div>
        <div className="max-h-40 overflow-y-auto p-3 space-y-2">
          {safeMessages.length === 0 ? (
            <p className="text-xs text-[#5C625F] text-center">Aucun message</p>
          ) : safeMessages.map((msg) => (
            <div key={msg.id} className={`p-2 rounded-lg text-xs ${msg.sender_role === "admin" ? "bg-[#2A4B46] text-white sm:ml-8" : "bg-gray-100 text-[#2A4B46] sm:mr-8"}`}>
              <p className="font-semibold">{msg.sender_name}</p>
              <p>{msg.message}</p>
              <p className="text-right opacity-60 mt-1" style={{ fontSize: "10px" }}>{new Date(msg.created_at).toLocaleString("fr-FR")}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 border-t border-[#E6E4E0] p-2">
          <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Écrire un message..." className="text-sm" data-testid="kyc-message-input"
            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()} />
          <Button size="sm" className="bg-[#2A4B46] text-white shrink-0" onClick={handleSendMessage} disabled={sendingMsg} data-testid="kyc-send-message">
            <Send size={14} />
          </Button>
        </div>
      </div>

      {/* Validation */}
      <div>
        <Label>Raison du rejet (si applicable)</Label>
        <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="mt-1" placeholder="Documents illisibles, informations incomplètes..." />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleValidate(true)} disabled={validating} data-testid="kyc-approve-button">
          <CheckCircle size={16} className="mr-2" /> {validating ? "..." : "Approuver"}
        </Button>
        <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleValidate(false)} disabled={validating || !rejectionReason} data-testid="kyc-reject-button">
          <XCircle size={16} className="mr-2" /> {validating ? "..." : "Rejeter"}
        </Button>
      </div>
    </div>
  );
}
